import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import {
    useToggle,
    UnstyledList,
    PageBehaviourButton,
    AnimatedExpandingContainer,
    Sizes,
} from '@ratehub/base-ui';

import PRODUCT_TYPES from '../definitions/ProductTypes';
import CreditCardShape from '../definitions/CreditCardShape';
import getCardLayoutFocusDetailsRows from '../functions/getCardLayoutFocusDetailsRows';


function CardLayoutFocusDetails({
    cardInfo,
    productType,
    isEqualizeHeight = false,
    className,
}) {
    const [ isShowingDetails, toggleIsShowingDetails ] = useToggle(false);

    const focusDetailsRows = getCardLayoutFocusDetailsRows(cardInfo, productType);
    const canShowMoreDetails = focusDetailsRows.some(row => row.rowDetails?.length);

    if (isEqualizeHeight) {
        // - as of now, focusDetailsRows is typically two or three items in length
        // - four is possible if reward cards have both welcome and anniversary bonuses active
        // - this optimization will pad 2 items to have an empty 3rd as a relatively simple way to equalize card height,
        //   without worrying about the case where another card in side-by-side might have 4 entries
        if (focusDetailsRows.length === 2) {
            focusDetailsRows.push({
                rowTitle: ' ',      // non-breaking space
                rowSummary: ' ',    // non-breaking space
                key: 'blank',
            });
        }
    }

    return (
        <div className={classNames(className, 'rh-position-relative')}>
            <If condition={canShowMoreDetails}>
                <PageBehaviourButton
                    className="show-more-or-less rh-position-absolute rh-pl-1 rh-ml-1 rh-fg-blueberry-dark"
                    onClick={toggleIsShowingDetails}
                    message={(
                        <FormattedMessage
                            id="edb.credit-card.focus-details.show-more-or-less"
                            defaultMessage="{isShowingDetails, select, true {show less -} other {see more +}}"
                            values={{
                                isShowingDetails: isShowingDetails,
                            }}
                        />
                    )}
                    size="extra-small"
                    data-name="card-toggle-details"
                />
            </If>

            <dl className="summary-info rh-text-s rh-my-0_5 rh-mx-1">
                <For
                    each="row"
                    of={focusDetailsRows}
                >
                    {/* one dl-content-wrapper per row prevents flex from wrapping three elements on to one line */}
                    <div
                        className="dl-content-wrapper rh-display-flex rh-flex-wrap rh-mb-0_5"
                        key={row.key}
                    >
                        <dt
                            className="row-title weight-medium"
                            data-test-name={`${row.key}.title`}
                        >
                            {row.rowTitle}
                        </dt>

                        <dd className="definitions-list rh-ml-0">
                            <p
                                className="description rh-mt-0 rh-mb-0"
                                data-test-name={row.key}
                            >
                                {row.rowSummary}
                            </p>

                            <If condition={row.rowDetails?.length}>
                                <AnimatedExpandingContainer
                                    className="expandable-details"
                                    isVisible={isShowingDetails}
                                >
                                    <UnstyledList
                                        className="rh-my-0_5"
                                        listItems={row.rowDetails}
                                    />
                                </AnimatedExpandingContainer>
                            </If>
                        </dd>
                    </div>
                </For>
            </dl>
        </div>
    );
}

CardLayoutFocusDetails.propTypes = {
    cardInfo: PropTypes.shape(CreditCardShape).isRequired,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)).isRequired,
    isEqualizeHeight: PropTypes.bool,
    className: PropTypes.string,
};


export default styled(observer(CardLayoutFocusDetails))`
    > .show-more-or-less {
        top: 0;
        right: ${Sizes.SPACING.ONE};
    }

    > .summary-info {
        > .dl-content-wrapper {
            gap: ${Sizes.SPACING.QUARTER};
            align-items: baseline;

            > .row-title {
                flex: 1 0 11em;
            }

            > .definitions-list { // dl.definitions-list
                flex: 100 1 24em;
            }
        }
    }
`;
